"use client";

import {
  createWebPlayerSharedStore,
  createWebPlayerTrackMeta,
  joinWebPlayerArtistNames,
} from "./webPlayerShared";
import type { WebPlayerTrackMeta } from "./webPlayerTypes";
import {
  describeAvailableMethods,
  hasRequiredMethods,
} from "@/hooks/webPlayerUtils";

export type SpotifySdkArtist = {
  name: string;
};

export type SpotifySdkImage = {
  url?: string | null;
};

export type SpotifySdkTrack = {
  id: string;
  uri: string;
  name: string;
  artists?: SpotifySdkArtist[];
  album?: {
    images?: SpotifySdkImage[];
  };
};

export type SpotifySdkState = {
  paused: boolean;
  position: number;
  duration: number;
  track_window: {
    current_track?: SpotifySdkTrack;
    next_tracks?: SpotifySdkTrack[];
  };
};

export type SpotifySdkError = {
  message?: string;
};

export type SpotifyPlayerStateResponse = {
  ok?: boolean;
  shuffle?: boolean | null;
  isPlaying?: boolean | null;
  deviceId?: string | null;
  error?: string | null;
};

export type SpotifyPlayerDevice = {
  id?: string | null;
  isActive?: boolean;
  isRestricted?: boolean;
  name?: string | null;
  type?: string | null;
};

export type SpotifyPlayerDevicesResponse = {
  ok?: boolean;
  devices?: SpotifyPlayerDevice[];
  error?: string | null;
};

export type SpotifyPlayerLike = {
  connect: () => Promise<boolean>;
  disconnect?: () => void;
  addListener: (event: string, callback: (...args: unknown[]) => void) => boolean;
  getCurrentState: () => Promise<SpotifySdkState | null>;
  getVolume?: () => Promise<number>;
  setVolume?: (volume: number) => Promise<void>;
  togglePlay: () => Promise<void>;
  nextTrack: () => Promise<void>;
  previousTrack: () => Promise<void>;
  seek: (ms: number) => Promise<void>;
  activateElement?: () => Promise<void>;
};

export type SpotifySharedState = {
  player: SpotifyPlayerLike | null;
  deviceId: string | null;
  isReady: boolean;
  isPlaying: boolean;
  currentTrack: WebPlayerTrackMeta | null;
  positionMs: number;
  durationMs: number;
  error: string | null;
  volume: number;
  needsTransfer: boolean;
  hasPlaybackState: boolean;
};

export type SpotifyPlayerErrorOptions = {
  markForReauth?: boolean;
  reason?: string;
  category?: "auth" | "playback";
  level?: "warn" | "error";
  event?: string;
  title?: string;
  details?: Record<string, unknown>;
  error?: unknown;
};

export type SpotifyEnsureReadyResult = {
  ready: boolean;
  deviceId: string | null;
  error: string | null;
};

export type SpotifyEnsureReadyFn = (
  timeoutMs?: number,
) => Promise<SpotifyEnsureReadyResult>;

export const REQUIRED_SPOTIFY_PLAYER_METHODS = [
  "connect",
  "addListener",
  "getCurrentState",
  "togglePlay",
  "nextTrack",
  "previousTrack",
  "seek",
] as const;

export const SPOTIFY_WEB_PLAYER_NAME = "IMAGS Web Player";

export const spotifySharedStore = createWebPlayerSharedStore<SpotifySharedState>({
  player: null,
  deviceId: null,
  isReady: false,
  isPlaying: false,
  currentTrack: null,
  positionMs: 0,
  durationMs: 0,
  error: null,
  volume: 0.8,
  needsTransfer: false,
  hasPlaybackState: false,
});

export function isUsableSpotifyPlayer(player: unknown): player is SpotifyPlayerLike {
  return hasRequiredMethods(player, REQUIRED_SPOTIFY_PLAYER_METHODS);
}

export function describeSpotifyPlayer(player: unknown) {
  return describeAvailableMethods(player, [
    ...REQUIRED_SPOTIFY_PLAYER_METHODS,
    "disconnect",
    "getVolume",
    "setVolume",
    "activateElement",
  ]);
}

export function disconnectSpotifyPlayer(player: SpotifyPlayerLike | null) {
  if (!player || typeof player.disconnect !== "function") return;

  try {
    player.disconnect();
  } catch {
    // Ignore disconnect failures and let the next init attempt replace the player.
  }
}

export function toSpotifyTrackMeta(
  track: SpotifySdkTrack | null | undefined
): WebPlayerTrackMeta | null {
  if (!track) return null;

  return createWebPlayerTrackMeta({
    id: track.id,
    uri: track.uri,
    title: track.name,
    artist: joinWebPlayerArtistNames(track.artists),
    imageUrl: track.album?.images?.[0]?.url ?? null,
  });
}

export function errorMessage(error: unknown, fallback: string) {
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

export async function resolveSpotifyPlaybackDeviceId({
  isReady,
  deviceId,
  ensureReady,
  playerError,
  timeoutMs = 3000,
  notReadyMessage = "Web player not ready yet. Try again in a moment.",
}: {
  isReady: boolean;
  deviceId: string | null;
  ensureReady?: SpotifyEnsureReadyFn;
  playerError: string | null;
  timeoutMs?: number;
  notReadyMessage?: string;
}) {
  if (isReady && deviceId) {
    return deviceId;
  }

  const readyResult = ensureReady
    ? await ensureReady(timeoutMs)
    : { ready: false, deviceId: null, error: playerError };

  if (readyResult.ready && readyResult.deviceId) {
    return readyResult.deviceId;
  }

  const detail = readyResult.error ?? playerError;
  throw new Error(
    detail ? `${notReadyMessage} ${detail}` : notReadyMessage,
  );
}

export function buildPlayerAuthErrorMessage(
  payloadError: string | null,
  status: number
) {
  if (payloadError === "missing_streaming_scope") {
    return "Spotify playback scope is missing. Reauthorize Spotify and try again.";
  }
  if (status === 401 || status === 409) {
    return "Spotify playback authorization expired. Reauthorize Spotify and try again.";
  }
  return `Failed to get Spotify token (HTTP ${status})`;
}

export function isMissingSpotifyDeviceResponse(status: number, bodyText: string) {
  if (status !== 404) return false;
  const normalized = bodyText.toLowerCase();
  return (
    normalized.includes("device not found") ||
    normalized.includes("no active device")
  );
}

export function wait(ms: number) {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

export function normalizeSpotifyDeviceName(name: string | null | undefined) {
  return typeof name === "string" ? name.trim().toLowerCase() : "";
}
